/**
 * Responsibles and accountant on the Obligations tab of an entity (ALC-4766): who prepares,
 * approves, transmits and pays each obligation, and the Taxpayer's accountant (contador
 * responsável). Namespace `entityObligationResponsibles`.
 */
export const entityObligationResponsiblesPtBr = {
  roles: {
    PREPARER: 'Prepara',
    APPROVER: 'Aprova',
    TRANSMITTER: 'Transmite',
    PAYER: 'Paga',
  },
  roleNames: {
    PREPARER: 'Quem prepara',
    APPROVER: 'Quem aprova',
    TRANSMITTER: 'Quem transmite',
    PAYER: 'Quem paga',
  },
  vacancy: {
    UNASSIGNED: 'Sem responsável',
    USER_NOT_FOUND: 'Usuário não encontrado',
    USER_INACTIVE: 'Usuário inativo',
    USER_NO_ENTITY_ACCESS: 'Sem acesso à entidade',
  },
  vacancyHelp: {
    UNASSIGNED: 'Ninguém foi definido para este papel.',
    USER_NOT_FOUND: 'O usuário definido não existe mais nesta empresa.',
    USER_INACTIVE: 'O usuário definido está inativo.',
    USER_NO_ENTITY_ACCESS: 'O usuário definido não tem mais acesso a esta entidade.',
  },
  state: {
    APPLICABLE: 'Aplicável',
    WAIVED_WITH_BASIS: 'Dispensada com fundamento',
    PENDING_CLASSIFICATION: 'Pendente de enquadramento',
    OUT_OF_SCOPE_FLAGGED: 'Fora do escopo, sinalizada',
  },
  readOnly: {
    notHeadOffice:
      'Os responsáveis padrão e os das obrigações federais valem para todo o contribuinte e são definidos na matriz.',
    noTaxpayer:
      'Esta entidade não tem contribuinte federal. Informe o contribuinte em "Informações básicas" para definir os responsáveis padrão.',
    noPermission: 'Você pode ver os responsáveis, mas não alterá-los.',
    openHeadOffice: 'Abrir a matriz',
    row: 'Definido na matriz',
    noCompanyAccess:
      'Para alterar os responsáveis da empresa, é preciso ter acesso a todos os estabelecimentos dela. Peça a quem administra o cadastro da empresa.',
    rowNoCompanyAccess: 'Sem acesso a toda a empresa',
  },
  responsibles: {
    title: 'Responsáveis',
    intro:
      'Defina quem prepara, aprova, transmite e paga. O padrão vale para todas as obrigações do contribuinte; você pode trocar o responsável de uma obrigação. O responsável precisa ser um usuário ativo com acesso à entidade: se deixar de ser, o papel fica sem responsável.',
    loadError: 'Não foi possível carregar os responsáveis desta entidade.',
    tableLabel: 'Responsáveis por obrigação',
    unassigned_one: '{{count}} obrigação aplicável sem responsável em alguma etapa',
    unassigned_other: '{{count}} obrigações aplicáveis sem responsável em alguma etapa',
    allAssigned: 'Todas as obrigações aplicáveis têm responsável em cada etapa.',
    empty: {
      title: 'Nenhuma obrigação para definir responsáveis',
      description: 'O catálogo de obrigações do sistema ainda não foi carregado.',
    },
  },
  defaults: {
    title: 'Responsáveis padrão do contribuinte',
    description: 'Valem para toda obrigação que não tem um responsável próprio.',
    none: 'Ninguém',
    saveError: 'Não foi possível salvar o responsável. Tente novamente.',
  },
  columns: {
    obligation: 'Obrigação',
    situation: 'Situação',
  },
  situation: {
    complete: 'Com responsáveis',
    vacant: 'Sem responsável em alguma etapa',
  },
  ownMark: 'própria',
  actions: {
    change: 'Alterar',
    changeLabel: 'Alterar responsáveis de {{name}}',
  },
  dialog: {
    title: 'Responsáveis de {{name}}',
    description:
      'Escolha quem exerce cada papel nesta obrigação. "Usar o padrão" volta ao responsável padrão do contribuinte.',
    useDefault: 'Usar o padrão do contribuinte',
    inactiveSuffix: '(indisponível)',
    cancel: 'Cancelar',
    submit: 'Salvar responsáveis',
    saveError: 'Não foi possível salvar os responsáveis. Tente novamente.',
  },
  accountant: {
    title: 'Contador responsável',
    description:
      'Quem assina as obrigações e os livros do contribuinte. Cada vigência vale a partir de uma data e encerra a anterior.',
    loadError: 'Não foi possível carregar o contador responsável.',
    retry: 'Tentar novamente',
    loading: 'Carregando o contador responsável',
    none: 'Nenhum contador responsável registrado para esta data.',
    current: 'Em vigor em {{date}}',
    name: 'Nome',
    crc: 'CRC',
    cpf: 'CPF',
    since: 'Desde',
    historyTitle: 'Vigências registradas',
    open: '{{from}} em diante',
    range: '{{from}} a {{to}}',
    register: 'Registrar contador',
    registerNew: 'Registrar novo contador',
    notHeadOffice: 'O contador responsável vale para todo o contribuinte e é registrado na matriz.',
    noCompanyAccess:
      'Para registrar o contador, é preciso ter acesso a todos os estabelecimentos da empresa. Peça a quem administra o cadastro da empresa.',
    noTaxpayer:
      'Esta entidade não tem contribuinte federal. Informe o contribuinte em "Informações básicas" para registrar o contador.',
    openHeadOffice: 'Abrir a matriz',
    dialog: {
      title: 'Registrar contador responsável',
      description:
        'A nova vigência começa na data informada e encerra a do contador anterior no dia anterior.',
      name: 'Nome completo',
      crc: 'Registro no CRC',
      crcPlaceholder: 'CRC-1SP123456/O-8',
      cpf: 'CPF',
      validFrom: 'Vigente a partir de',
      validFromPlaceholder: 'dd/mm/aaaa',
      validFromError: 'Informe uma data válida.',
      cpfError: 'Informe os 11 dígitos do CPF.',
      cancel: 'Cancelar',
      submit: 'Registrar',
      saveError: 'Não foi possível registrar o contador. Tente novamente.',
    },
  },
  errors: {
    OBLIGATION_NOT_FOUND: 'Esta obrigação não está no catálogo. Atualize a página.',
    OBLIGATION_DATE_INVALID: 'Informe datas válidas.',
    OBLIGATION_RESPONSIBLE_NOT_ELIGIBLE:
      'O responsável precisa ser um usuário ativo da empresa com acesso a esta entidade. Escolha outra pessoa ou conceda o acesso antes.',
    TAXPAYER_ACCOUNTANT_DETAILS_REQUIRED: 'Informe o nome, o registro no CRC e o CPF do contador.',
    TAXPAYER_ACCOUNTANT_CPF_INVALID: 'O CPF do contador não é válido. Confira os 11 dígitos.',
    TAXPAYER_ACCOUNTANT_OVERLAP:
      'Já existe um contador registrado nesta data. Escolha uma data de início diferente.',
    TAXPAYER_ACCOUNTANT_PERIOD_LOCKED:
      'Uma apuração aprovada ou uma obrigação já transmitida usa este período. Escolha uma data de início posterior.',
    TAXPAYER_ACCOUNTANT_MISSING: 'Não há contador responsável registrado para esta data.',
    OBLIGATION_RETRY: 'Os dados mudaram durante o salvamento. Atualize a página e tente novamente.',
    ESTABLISHMENT_PROFILE_ENTITY_NOT_FOUND: 'Não encontramos o estabelecimento. Atualize a página.',
    ESTABLISHMENT_PROFILE_TAXPAYER_NOT_FOUND:
      'Não encontramos o contribuinte federal. Atualize a página.',
    ESTABLISHMENT_PROFILE_TAXPAYER_REQUIRED:
      'Esta entidade não tem contribuinte federal. Informe o contribuinte em "Informações básicas".',
    ESTABLISHMENT_PROFILE_HEAD_OFFICE_ONLY:
      'Estes dados valem para todo o contribuinte. Abra a matriz para alterar.',
    ESTABLISHMENT_PROFILE_ENTITY_SCOPE_FORBIDDEN:
      'Este estabelecimento está fora do seu escopo de acesso. Peça a um administrador para ampliar o acesso.',
  },
} as const;

type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> };

export const entityObligationResponsiblesEn: Widen<typeof entityObligationResponsiblesPtBr> = {
  roles: {
    PREPARER: 'Prepares',
    APPROVER: 'Approves',
    TRANSMITTER: 'Transmits',
    PAYER: 'Pays',
  },
  roleNames: {
    PREPARER: 'Who prepares',
    APPROVER: 'Who approves',
    TRANSMITTER: 'Who transmits',
    PAYER: 'Who pays',
  },
  vacancy: {
    UNASSIGNED: 'No responsible',
    USER_NOT_FOUND: 'User not found',
    USER_INACTIVE: 'User inactive',
    USER_NO_ENTITY_ACCESS: 'No access to the entity',
  },
  vacancyHelp: {
    UNASSIGNED: 'Nobody was set for this role.',
    USER_NOT_FOUND: 'The user who was set no longer exists in this company.',
    USER_INACTIVE: 'The user who was set is inactive.',
    USER_NO_ENTITY_ACCESS: 'The user who was set no longer has access to this entity.',
  },
  state: {
    APPLICABLE: 'Applicable',
    WAIVED_WITH_BASIS: 'Waived with basis',
    PENDING_CLASSIFICATION: 'Pending classification',
    OUT_OF_SCOPE_FLAGGED: 'Out of scope, flagged',
  },
  readOnly: {
    notHeadOffice:
      'The default responsibles and those of federal obligations apply to the whole Taxpayer and are set on the head office.',
    noTaxpayer:
      'This entity has no federal Taxpayer. Set the Taxpayer in "Basic information" to define the default responsibles.',
    noPermission: 'You can see the responsibles but not change them.',
    openHeadOffice: 'Open the head office',
    row: 'Set on the head office',
    noCompanyAccess:
      'To change the responsibles of the company, you need access to every establishment of it. Ask the person who manages the company record.',
    rowNoCompanyAccess: 'No access to the whole company',
  },
  responsibles: {
    title: 'Responsibles',
    intro:
      'Set who prepares, approves, transmits and pays. The default applies to every obligation of the Taxpayer; you can change the responsible of one obligation. The responsible must be an active user with access to the entity: if they stop being one, the role has no responsible.',
    loadError: 'Could not load the responsibles of this entity.',
    tableLabel: 'Responsibles by obligation',
    unassigned_one: '{{count}} applicable obligation with no responsible at some step',
    unassigned_other: '{{count}} applicable obligations with no responsible at some step',
    allAssigned: 'Every applicable obligation has a responsible at each step.',
    empty: {
      title: 'No obligations to set responsibles for',
      description: 'The system obligation catalog has not been loaded yet.',
    },
  },
  defaults: {
    title: 'Taxpayer default responsibles',
    description: 'They apply to every obligation that has no responsible of its own.',
    none: 'Nobody',
    saveError: 'Could not save the responsible. Try again.',
  },
  columns: {
    obligation: 'Obligation',
    situation: 'Status',
  },
  situation: {
    complete: 'Responsibles set',
    vacant: 'No responsible at some step',
  },
  ownMark: 'own',
  actions: {
    change: 'Change',
    changeLabel: 'Change responsibles of {{name}}',
  },
  dialog: {
    title: 'Responsibles of {{name}}',
    description:
      'Choose who holds each role for this obligation. "Use the default" goes back to the Taxpayer default responsible.',
    useDefault: 'Use the Taxpayer default',
    inactiveSuffix: '(unavailable)',
    cancel: 'Cancel',
    submit: 'Save responsibles',
    saveError: 'Could not save the responsibles. Try again.',
  },
  accountant: {
    title: 'Accountant in charge',
    description:
      'Who signs the Taxpayer obligations and books. Each validity starts on a date and ends the previous one.',
    loadError: 'Could not load the accountant in charge.',
    retry: 'Try again',
    loading: 'Loading the accountant in charge',
    none: 'No accountant in charge registered for this date.',
    current: 'In force on {{date}}',
    name: 'Name',
    crc: 'CRC',
    cpf: 'CPF',
    since: 'Since',
    historyTitle: 'Registered validities',
    open: '{{from}} onwards',
    range: '{{from}} to {{to}}',
    register: 'Register accountant',
    registerNew: 'Register new accountant',
    noCompanyAccess:
      'To register the accountant, you need access to every establishment of the company. Ask the person who manages the company record.',
    notHeadOffice:
      'The accountant in charge applies to the whole Taxpayer and is registered on the head office.',
    noTaxpayer:
      'This entity has no federal Taxpayer. Set the Taxpayer in "Basic information" to register the accountant.',
    openHeadOffice: 'Open the head office',
    dialog: {
      title: 'Register accountant in charge',
      description:
        'The new validity starts on the date you give and ends the previous accountant the day before.',
      name: 'Full name',
      crc: 'CRC registration',
      crcPlaceholder: 'CRC-1SP123456/O-8',
      cpf: 'CPF',
      validFrom: 'In force from',
      validFromPlaceholder: 'dd/mm/yyyy',
      validFromError: 'Enter a valid date.',
      cpfError: 'Enter the 11 digits of the CPF.',
      cancel: 'Cancel',
      submit: 'Register',
      saveError: 'Could not register the accountant. Try again.',
    },
  },
  errors: {
    OBLIGATION_NOT_FOUND: 'This obligation is not in the catalog. Refresh the page.',
    OBLIGATION_DATE_INVALID: 'Enter valid dates.',
    OBLIGATION_RESPONSIBLE_NOT_ELIGIBLE:
      'The responsible must be an active user of the company with access to this entity. Pick someone else or grant the access first.',
    TAXPAYER_ACCOUNTANT_DETAILS_REQUIRED: 'Enter the accountant name, CRC registration and CPF.',
    TAXPAYER_ACCOUNTANT_CPF_INVALID: 'The accountant CPF is not valid. Check the 11 digits.',
    TAXPAYER_ACCOUNTANT_OVERLAP:
      'An accountant is already registered on this date. Pick a different start date.',
    TAXPAYER_ACCOUNTANT_PERIOD_LOCKED:
      'An approved assessment or an obligation already transmitted uses this period. Pick a later start date.',
    TAXPAYER_ACCOUNTANT_MISSING: 'No accountant in charge is registered for this date.',
    OBLIGATION_RETRY: 'The data changed while saving. Refresh the page and try again.',
    ESTABLISHMENT_PROFILE_ENTITY_NOT_FOUND:
      'We could not find the establishment. Refresh the page.',
    ESTABLISHMENT_PROFILE_TAXPAYER_NOT_FOUND:
      'We could not find the federal Taxpayer. Refresh the page.',
    ESTABLISHMENT_PROFILE_TAXPAYER_REQUIRED:
      'This entity has no federal Taxpayer. Set the Taxpayer in "Basic information".',
    ESTABLISHMENT_PROFILE_HEAD_OFFICE_ONLY:
      'This data applies to the whole Taxpayer. Open the head office to change it.',
    ESTABLISHMENT_PROFILE_ENTITY_SCOPE_FORBIDDEN:
      'This establishment is outside your access scope. Ask an administrator to widen your access.',
  },
};
